import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ProfileApiI, ProfileI } from "../../pojo/ProfileI";
import { TimeModelI } from "../../pojo/TimeModelI";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25RadioModule } from "../s25-radio/s25.radio.module";
import { PatternEndChoices, ProfileUtil } from "./profile.util";

@TypeManagerDecorator("s25-ng-date-pattern")
@Component({
    selector: "s25-ng-date-pattern",
    template: `
        @if (init) {
            <div [attr.data-type]="type">
                @if (overlapError) {
                    <span class="ngBold ngRed">{{ overlapError }}</span>
                }
                <div class="pattern-row pattern-row--active">
                    <label class="labelMinWidth" [attr.for]="'repeatsEvery-' + id">Repeats every</label>
                    <s25-generic-dropdown
                        [items]="repeatsOptions"
                        [(value)]="profileModel.repeatsEvery"
                        (valueChange)="repeatsEveryChange()"
                        [searchEnabled]="false"
                        class="maxWidth200"
                        [attr.id]="'repeatsEvery-' + id"
                    />
                </div>
                @if (type === "weekly") {
                    <fieldset class="pattern-row pattern-row--active">
                        <legend class="repeats-on">Repeats on</legend>
                        @for (dow of profileModel.daysOfWeek; track dow) {
                            <s25-ng-checkbox
                                [(value)]="dow.chosen"
                                (valueChange)="onChange()"
                                [labelId]="dow.itemName"
                                [labelClass]="'inline'"
                                ><span>{{ dow.itemName }}</span></s25-ng-checkbox
                            >
                        }
                    </fieldset>
                }
                @if (type === "monthly") {
                    <fieldset class="repeat-by-group">
                        <legend class="visuallyhidden">Repeat by</legend>
                        <div class="pattern-row" [class.pattern-row--active]="profileModel.repeatBy === 'day'">
                            <label>
                                <s25-ng-radio
                                    [radioValue]="'day'"
                                    [(value)]="profileModel.repeatBy"
                                    [name]="'repeatBy-' + id"
                                    (valueChange)="checkOverlap()"
                                    class="labelMinWidth"
                                >
                                    <span class="ngInlineBlock">Repeat by Day</span>
                                </s25-ng-radio>
                            </label>
                            @for (rep of profileModel.repeatByDay; track rep) {
                                <div class="repeat-interval">
                                    <label>
                                        <span>Repeat</span>
                                        <s25-generic-dropdown
                                            [items]="repeatByDayStarts"
                                            [(value)]="rep.repeatStart"
                                            (valueChange)="checkOverlap()"
                                            [searchEnabled]="false"
                                        />
                                    </label>
                                    <label>
                                        <span>from</span>
                                        <s25-generic-dropdown
                                            [items]="repeatByDayFroms"
                                            [(value)]="rep.repeatFrom"
                                            (valueChange)="checkOverlap()"
                                            [searchEnabled]="false"
                                        />
                                    </label>
                                    <button
                                        class="remove-repeat aw-button aw-button--danger aw-button--flat"
                                        (click)="repeatByDayRemove(rep)"
                                    >
                                        Remove Day
                                    </button>
                                </div>
                            }
                            <button class="aw-button aw-button--outline" (click)="addRepeatByDay()">
                                Add Repeat Day
                            </button>
                        </div>
                        <div class="pattern-row" [class.pattern-row--active]="profileModel.repeatBy === 'position'">
                            <label>
                                <s25-ng-radio
                                    [radioValue]="'position'"
                                    [(value)]="profileModel.repeatBy"
                                    [name]="'repeatBy-' + id"
                                    (valueChange)="checkOverlap()"
                                    class="labelMinWidth"
                                >
                                    <span class="ngInlineBlock">Repeat by Position</span>
                                </s25-ng-radio>
                            </label>
                            @for (rep of profileModel.repeatByPosition; track rep) {
                                <fieldset class="repeat-interval repeat-by-position">
                                    <legend class="visuallyhidden">Repeat by position</legend>
                                    <div>
                                        <label>
                                            <span>Repeat on the</span>
                                        </label>
                                        <s25-generic-dropdown
                                            class="position"
                                            [items]="repeatByPositionStarts"
                                            [(value)]="rep.repeatStart"
                                            (valueChange)="checkOverlap()"
                                            [searchEnabled]="false"
                                        />
                                    </div>
                                    <s25-generic-dropdown
                                        [items]="repeatByPositionFroms"
                                        [(value)]="rep.repeatFrom"
                                        (valueChange)="checkOverlap()"
                                        [searchEnabled]="false"
                                    />
                                    <button
                                        class="remove-repeat aw-button aw-button--danger aw-button--flat"
                                        (click)="repeatByPositionRemove(rep)"
                                    >
                                        Remove Position
                                    </button>
                                </fieldset>
                            }
                            <button class="aw-button aw-button--outline" (click)="addRepeatByPosition()">
                                Add Repeat Position
                            </button>
                        </div>
                    </fieldset>
                }
                @if (hasAnyEndDt || hasDateEndDt || hasIterations) {
                    <fieldset class="repeat-end-group">
                        <legend class="visuallyhidden">Repeat end</legend>
                        @if (hasAnyEndDt) {
                            <div class="pattern-row" [class.pattern-row--active]="profileModel.repeatEnd === 'none'">
                                <label>
                                    <s25-ng-radio
                                        [radioValue]="'none'"
                                        [(value)]="profileModel.repeatEnd"
                                        [name]="'repeatUntil-' + id"
                                        class="labelMinWidth"
                                        (valueChange)="onChange()"
                                    >
                                        <span>Any end date</span>
                                    </s25-ng-radio>
                                </label>
                            </div>
                        }
                        @if (hasDateEndDt) {
                            <div class="pattern-row" [class.pattern-row--active]="profileModel.repeatEnd === 'date'">
                                <label>
                                    <s25-ng-radio
                                        [radioValue]="'date'"
                                        [(value)]="profileModel.repeatEnd"
                                        [name]="'repeatUntil-' + id"
                                        class="labelMinWidth"
                                        (valueChange)="onChange()"
                                    >
                                        <span>Repeats through</span>
                                    </s25-ng-radio>
                                </label>
                                <s25-ng-editable-date
                                    class="c-displayInlineBlock"
                                    [(val)]="repeatsThroughBean.date"
                                    (valChange)="repeatsThroughDateChange()"
                                    [alwaysEditing]="true"
                                    [popoverOnBody]="true"
                                ></s25-ng-editable-date>
                                @if (dateError) {
                                    <div class="ngBold ngRed">{{ dateError }}</div>
                                }
                            </div>
                        }
                        @if (hasIterations) {
                            <div
                                class="pattern-row"
                                [class.pattern-row--active]="profileModel.repeatEnd === 'iterations'"
                            >
                                <label>
                                    <s25-ng-radio
                                        [radioValue]="'iterations'"
                                        [(value)]="profileModel.repeatEnd"
                                        [name]="'repeatUntil-' + id"
                                        class="labelMinWidth"
                                        (valueChange)="onChange()"
                                    >
                                        <span>Ends after</span>
                                    </s25-ng-radio>
                                </label>
                                <div class="iterations">
                                    <input
                                        type="number"
                                        [(ngModel)]="profileModel.endsAfter"
                                        (change)="endsAfterChange()"
                                        class="c-input iteration-input"
                                    />
                                    <span>iterations</span>
                                </div>
                            </div>
                        }
                    </fieldset>
                }
            </div>
        }
    `,
    styles: `
        .maxWidth200 {
            max-width: 200px;
            display: block;
        }

        .labelMinWidth {
            min-width: 11em;
            display: block;
        }

        .repeat-by-position {
            display: flex;
            gap: 0.625rem;
            align-items: flex-end;
            flex-wrap: wrap;

            .position {
                min-width: 11em;
                display: block;
            }
        }

        .pattern-row {
            display: flex;
            align-items: center;
            gap: 0.5em;
            padding: 0.75em;
            flex-wrap: wrap;
            border: 1px solid transparent;
            border-radius: var(--border-radius);
            transition:
                border-color var(--transition-medium),
                background-color var(--transition-medium);

            &.pattern-row--active {
                border-color: var(--color--border);
                background-color: var(--color--page-bg);
            }
            > label {
                margin: 0.3125em 0;
            }
            + .pattern-row {
                margin-top: 0.5em;
            }

            &:where(fieldset) {
                margin: 0;
                min-inline-size: 0;
            }
        }

        .repeat-by-group,
        .repeat-end-group {
            border: 0;
            margin: 0;
            padding: 0;
            min-inline-size: 0;

            > .pattern-row + .pattern-row {
                margin-top: 0.5em;
            }
        }

        .pattern-row + .repeat-by-group,
        .pattern-row + .repeat-end-group,
        .repeat-by-group + .repeat-end-group,
        .pattern-row + fieldset.pattern-row,
        fieldset.pattern-row + .repeat-end-group {
            margin-top: 0.5em;
        }

        .repeat-interval {
            display: flex;
            gap: 0.5em;
            flex-wrap: wrap;
            align-items: flex-end;
            width: 100%;
            margin-bottom: 0.125em;

            &:where(fieldset) {
                border: 0;
                padding: 0;
                min-inline-size: 0;
            }

            .remove-repeat {
                margin-bottom: 0.125em;
            }
            + .repeat-interval {
                border-top: 1px solid var(--color--border);
                padding-top: 0.625em;
            }
        }

        .repeats-on {
            width: 100%;
            float: left;
            padding: 0;
            margin: 0;
            font-size: inherit;
            font-weight: inherit;
        }

        .iterations > input {
            width: 5em !important;
            margin-right: 0.5em;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25DropdownPaginatedModule, S25CheckboxModule, S25RadioModule, S25EditableModule, FormsModule],
})
export class S25DatePatternComponent implements OnInit {
    @Input() type: "daily" | "weekly" | "monthly";
    @Input() profileModel: ProfileI;
    @Input() timeModel: TimeModelI; //Acts as the start date for the profile
    @Input() profileCode: string;
    @Input() apiBean: ProfileApiI;
    @Input() patternEndChoices: PatternEndChoices[] = ["date", "iterations"]; //none, date, iterations

    @Output() apiBeanChange = new EventEmitter<ProfileApiI>();

    static patternCount: number = 0;

    init: boolean;
    dateError: string;
    overlapError: string;
    id: string;
    hasIterations: boolean;
    hasAnyEndDt: boolean;
    hasDateEndDt: boolean;

    constructor(private cd: ChangeDetectorRef) {
        S25DatePatternComponent.patternCount++;
        this.id = "ngDatePattern" + S25DatePatternComponent.patternCount;
    }

    repeatsThroughBean: any = {};
    repeatsOptions: any[];
    repeatByDayStarts: any[];
    repeatByDayFroms = [
        { itemName: "Start of the Month", itemId: "start" },
        { itemName: "End of the Month", itemId: "end" },
    ];
    repeatByPositionStarts = [
        { itemName: "First", itemId: 1 },
        { itemName: "Second", itemId: 2 },
        { itemName: "Third", itemId: 3 },
        { itemName: "Fourth", itemId: 4 },
        { itemName: "Fifth", itemId: 5 },

        { itemName: "Last", itemId: -1 },
        { itemName: "Second-to-last", itemId: -2 },
        { itemName: "Third-to-last", itemId: -3 },
        { itemName: "Fourth-to-last", itemId: -4 },
        { itemName: "Fifth-to-last", itemId: -5 },
    ];
    repeatByPositionFroms = [
        { itemName: "Sunday", abbr: "SU", itemId: 0 },
        { itemName: "Monday", abbr: "MO", itemId: 1 },
        { itemName: "Tuesday", abbr: "TU", itemId: 2 },
        { itemName: "Wednesday", abbr: "WE", itemId: 3 },
        { itemName: "Thursday", abbr: "TH", itemId: 4 },
        { itemName: "Friday", abbr: "FR", itemId: 5 },
        { itemName: "Saturday", abbr: "SA", itemId: 6 },
    ];
    resetErrors() {
        this.dateError = "";
        this.overlapError = "";
    }
    repeatByDayRemove(rep: any) {
        S25Util.array.inplaceRemoveByProp(this.profileModel.repeatByDay, "itemId", rep.itemId);
        this.onChange();
    }
    repeatByPositionRemove(rep: any) {
        S25Util.array.inplaceRemoveByProp(this.profileModel.repeatByPosition, "itemId", rep.itemId);
        this.onChange();
    }
    addRepeatByDay() {
        this.profileModel.repeatBy = "day";
        this.profileModel.repeatByDay ??= [];
        this.profileModel.repeatByDay.push({
            itemId: ProfileUtil.getNextCounter(),
            repeatStart: this.repeatByDayStarts[0],
            repeatFrom: this.repeatByDayFroms[0],
        });
        this.checkOverlap();
        this.onChange();
    }
    addRepeatByPosition() {
        this.profileModel.repeatBy = "position";
        this.profileModel.repeatByPosition ??= [];
        this.profileModel.repeatByPosition.push({
            itemId: ProfileUtil.getNextCounter(),
            repeatStart: this.repeatByPositionStarts[0],
            repeatFrom: this.repeatByPositionFroms[0],
        });
        this.checkOverlap();
        this.onChange();
    }
    checkOverlap() {
        if (ProfileUtil.hasOverlaps(this.timeModel, this.profileModel, this.repeatsThroughBean.date)) {
            this.overlapError =
                "Occurrence dates are overlapping. This can happen when repeating an overnight occurrence or from pre/setup/post/takedown times causing the occurrence to span midnight";
        } else {
            this.overlapError = "";
            this.onChange();
        }
    }

    repeatsEveryChange() {
        if (ProfileUtil.hasOverlaps(this.timeModel, this.profileModel, this.repeatsThroughBean.date)) {
            this.overlapError =
                "Occurrence dates are overlapping. This can happen when repeating an overnight occurrence or from pre/setup/post/takedown times causing the occurrence to span midnight";
            this.profileModel.repeatsEvery = this.repeatsOptions[0];
        }
        this.cd.detectChanges();
        this.onChange();
    }
    repeatsThroughDateChange() {
        this.resetErrors();
        this.profileModel.repeatEnd = "date";
        if (S25Util.date.getDate(this.timeModel.evStartDt) > S25Util.date.getDate(this.repeatsThroughBean.date)) {
            this.dateError =
                "When repeating through to a final date, please choose a final date that is greater than the initial start date";
            ProfileUtil.resetThroughDate(this.repeatsThroughBean, this.profileModel, null, this.timeModel);
        }
        if (ProfileUtil.hasOverlaps(this.timeModel, this.profileModel, this.repeatsThroughBean.date)) {
            this.overlapError =
                "Occurrence dates are overlapping. This can happen when repeating an overnight occurrence or from pre/setup/post/takedown times causing the occurrence to span midnight";
            ProfileUtil.resetThroughDate(this.repeatsThroughBean, this.profileModel, null, this.timeModel);
        }

        if (!this.dateError && !this.overlapError) {
            this.profileModel.throughDate = this.repeatsThroughBean.date;
            ProfileUtil.setThroughDate(this.repeatsThroughBean, this.profileModel, null);
            this.onChange();
        }
    }
    endsAfterChange() {
        this.resetErrors();
        this.profileModel.repeatEnd = "iterations";
        if (S25Util.isDefined(this.profileModel.endsAfter) && this.profileModel.endsAfter <= 0) {
            this.profileModel.endsAfter = 1;
        }

        if (ProfileUtil.hasOverlaps(this.timeModel, this.profileModel, this.repeatsThroughBean.date)) {
            this.overlapError =
                "Occurrence dates are overlapping. This can happen when repeating an overnight occurrence or from setup/pre/post/takedown times causing the occurrence to span midnight";
            this.profileModel.endsAfter = 1;
        }
        this.onChange();
    }

    onChange() {
        this.apiBeanChange.emit(this.apiBean);
    }

    ngOnInit(): void {
        this.hasIterations = this.patternEndChoices.includes("iterations");
        this.hasAnyEndDt = this.patternEndChoices.includes("none");
        this.hasDateEndDt = this.patternEndChoices.includes("date");
        this.repeatsThroughBean = {};
        this.repeatsOptions = [];
        for (let i = 1; i < 13; i++) {
            this.repeatsOptions.push({
                itemName:
                    (i === 1 ? "" : i === 2 ? "other " : i === 3 ? "3rd " : i + "th ") +
                    (this.type === "daily" ? "day" : this.type === "weekly" ? "week" : "month"),
                itemId: i,
            });
        }

        this.repeatByDayStarts = [];
        for (let i = 0; i < 31; i++) {
            this.repeatByDayStarts.push({
                itemName: i + " day" + (i !== 1 ? "s" : ""),
                itemId: i + 1, //add one to be compatible with profile codes since they save from 1 - 31, not 0 - 30
            });
        }

        this.profileModel = Object.assign(
            ProfileUtil.getProfileModel(
                this.type,
                this.profileCode,
                ProfileUtil.getDefaultThroughDate(this.timeModel),
                this.timeModel.evStartDt,
            ),
            this.profileModel,
        );

        ProfileUtil.setThroughDate(this.repeatsThroughBean, this.profileModel, null);
        this.apiBean.getProfileModel = function () {
            return this.profileModel;
        }.bind(this);
        this.apiBean.getProfileCode = () => {
            return ProfileUtil.getProfileCodeByModel(this.profileModel);
        };
        this.apiBean.getOccurrences = () => {
            return ProfileUtil.getOccurrences(this.timeModel, this.profileModel);
        };

        this.cd.detectChanges();
        this.init = true;
    }
}
